import * as React from 'react';
import {useSelector} from '@xstate/react';
import {zodResolver} from '@hookform/resolvers/zod';
import {FormProvider, useForm} from 'react-hook-form';
import useLabelTranslation from 'src/hooks/useLabelTranslation';

import {FormInput} from '../../lib/form-input';
import {BrandButton} from '../../lib/brand-button/brand-button';
import {SubTitleMedium} from '@spaced-out/ui-design-system/lib/components/Text';
import {InContextAlert} from '@spaced-out/ui-design-system/lib/components/InContextAlert';

import {useRootMachine} from '../../hooks/useRootMachine';
import {useBookingMachine} from '../../hooks/useBookingMachine';
import {
  selectBookingFormInputs,
  selectAdditionalQuestions,
} from '../../selectors/booking-selector';

import {getFormSchema} from './booking-form.schema';
import {selectCountryCode, selectError} from '../../selectors/commonSelector';
import {getDefaultFormValues} from '../../utils/form';
import {selectSession} from '../../selectors/session-selector';
import {Button} from '@spaced-out/ui-design-system/lib/components/Button';

import {TEST_ID} from '../../utils/testId';
import {generateTestId} from '@spaced-out/ui-design-system/lib/utils/qa';

import css from './booking-form.css';


export const BookingForm = () => {
  const {t, language} = useLabelTranslation();

  const {bookingActor, bookingState} = useBookingMachine();
  const {rootActor} = useRootMachine();
  const {errorMessage, allowRetry} = useSelector(bookingActor, (state) =>
    selectError(state, t),
  );

  const session = useSelector(rootActor, (state) => selectSession(state));
  const formInputs = useSelector(bookingActor, (state) =>
    selectBookingFormInputs(state, t),
  );
  const countyCode = useSelector(rootActor, (state) =>
    selectCountryCode(state),
  );
  const additionalQuestions = useSelector(bookingActor, (state) =>
    selectAdditionalQuestions(state),
  );

  const formSchema = getFormSchema({items: formInputs, countyCode, t});

  const {defaultValues, disabledFields} = getDefaultFormValues(
    formInputs,
    session,
  );

  const methods = useForm({
    defaultValues,
    resolver: zodResolver(formSchema),
    mode: 'onBlur',
  });

  // recreate form when language changes because React Hook Form caches error messages in its internal state
  React.useEffect(() => {
    methods.reset();
  }, [language, methods]);

  const onSubmit = () => {
    const formValues = methods.getValues();
    const {guestName, guestEmail, guestPhone, ...restQuestions} = formValues;

    const updatedAdditionalQuestionAnswers = Object.entries(restQuestions).map(
      ([_key, value], index) => ({
        question: additionalQuestions[index]?.question || '',
        answer: value as string,
      }),
    );

    bookingActor.send({
      type: 'form.submit',
      guestName: guestName as string,
      guestEmail: guestEmail as string,
      guestPhone: guestPhone as string,
      additionalQuestionAnswers: updatedAdditionalQuestionAnswers,
    });
  };

  const handleBack = () => {
    bookingActor.send({type: 'bookingForm.back'});
  };

  const isBookingFormSubmitting = bookingState.matches({
    bookingForm: 'submitting',
  });
  const isSubmitting = bookingState.matches({bookingForm: 'submitting'});
  const hasError = bookingState.matches({bookingForm: 'retrySubmit'});

  return (
    <FormProvider {...methods}>
      <form
        className={css.formContainer}
        onSubmit={methods.handleSubmit(onSubmit)}
        data-testid={generateTestId({
          base: TEST_ID.BOOKING_FLOW,
          slot: 'booking-form',
        })}
      >
        <div className={css.formContent}>
          <div className={css.questionContainer}>
            <SubTitleMedium>
              {t(
                'Please_answer_the_following_questions',
                'Please answer the following questions',
              )}
            </SubTitleMedium>
            {formInputs.map((item, index) => (
              <FormInput
                key={item.name}
                item={item}
                disabled={disabledFields[item.name]}
                testId={generateTestId({
                  base: TEST_ID.BOOKING_FLOW,
                  slot: 'form-input',
                  index: index.toString(),
                })}
              />
            ))}
          </div>
        </div>
        <div className={css.submitButtonSection}>
          <div className={css.buttonContainer}>
            <Button
              type="tertiary"
              size="medium"
              iconLeftName="chevron-left"
              onClick={handleBack}
              disabled={isBookingFormSubmitting}
              testId={generateTestId({
                base: TEST_ID.BOOKING_FLOW,
                slot: 'back-button',
              })}
            >
              {t('Back', 'Back')}
            </Button>
            <BrandButton
              actionType="submit"
              size="medium"
              disabled={hasError && !allowRetry}
              isLoading={isSubmitting}
              iconRightName="check"
              testId={generateTestId({
                base: TEST_ID.BOOKING_FLOW,
                slot: 'schedule-meeting-button',
              })}
            >
              {t('Schedule_Meeting', 'Schedule Meeting')}
            </BrandButton>
          </div>
          {hasError && (
            <InContextAlert
              leftIconName="shield-exclamation"
              semantic="danger"
              testId={generateTestId({
                base: TEST_ID.BOOKING_FLOW,
                slot: 'in-context-alert-danger',
              })}
            >
              {/* already translated in errorMessage */}
              {/* eslint-disable-next-line @spaced-out/i18n/no-dynamic-labels */}
              {errorMessage}
            </InContextAlert>
          )}
        </div>
      </form>
    </FormProvider>
  );
};
